/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000120 営業所照会
 */

import { Column, Query } from "@material-table/core";
import { B000120Find, B000120FindRequest, catchRestError } from "clients/rest";
import {
  SText,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  eigyoCdFrom: string;
  eigyoCdTo: string;
  eigyoNm: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: number;
  eigyoCd: string;
  eigyoNm: string;
  eigyoKana: string;
  eigyoNmr: string;
  telNo: string;
  faxNo: string;
  danpyoinji1: string;
  denpyoinji2: string;
  denpyoinji3: string;
  denpyoinji4: string;
  denpyoinji5: string;
  ginkoCd1: string;
  ginkoshitenCd1: string;
  kozashubetsu1: number;
  kozabango1: string;
  ginkoCd2: string;
  ginkoshitenCd2: string;
  kozashubetsu2: number;
  kozaabango2: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  eigyoCdFrom: "",
  eigyoCdTo: "",
  eigyoNm: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "2rem", align: "right" },
  { field: "eigyoCd", title: "営業コード", width: "6rem" },
  { field: "eigyoNm", title: "営業所名", width: "20rem" },
  { field: "eigyoKana", title: "営業所カナ", width: "20rem" },
  { field: "eigyoNmr", hidden: true },
  { field: "telNo", hidden: true },
  { field: "faxNo", hidden: true },
  { field: "denpyoinji1", hidden: true },
  { field: "denpyoinji2", hidden: true },
  { field: "denpyoinji3", hidden: true },
  { field: "denpyoinji4", hidden: true },
  { field: "denpyoinji5", hidden: true },
  { field: "ginkoCd1", hidden: true },
  { field: "ginkoshitenCd1", hidden: true },
  { field: "kozashubetsu1", hidden: true },
  { field: "kozabango1", hidden: true },
  { field: "ginkoCd2", hidden: true },
  { field: "ginkoshitenCd2", hidden: true },
  { field: "kozashubetsu2", hidden: true },
  { field: "kozabango2", hidden: true },
];

const focusId = "B000120";

// B000120 営業所照会
export const B000120 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用フラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログの開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  //フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    // 初回フォーカス先
    // 処理詳細１
    focus(1);
  }, []);

  // 検索ボタンクリックイベント
  const handleCliclSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;
      // 検索パラメータを作成する。
      const param: B000120FindRequest = {
        eigyoCdFrom: fields.eigyoCdFrom,
        eigyoCdTo: fields.eigyoCdTo,
        name: fields.eigyoNm,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        // ["出力項目名,順番(asc/desc", ...]
        sort: ["eigyoCd,asc"],
      };
      // API呼び出し
      B000120Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame title="営業所照会" gridTemplateColumns="6rem 6rem 1rem 6rem 2rem">
        <SLabel>営業所コード</SLabel>
        <SNumberField name="eigyoCdFrom" code size={6} {...register(1)} />
        <SText>～</SText>
        <SNumberField name="eigyoCdTo" code size={6} {...register(2)} />
        <SLabel gridColumn="1">営業所名</SLabel>
        <STextField
          name="eigyoNm"
          size={40}
          gridColumn="span 3"
          {...register(3)}
        />
        <SNormalButton
          label={labels.buttons.search}
          gridRow="3"
          gridColumn="5"
          {...register(4)}
          onClick={handleCliclSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
